import { supabase } from '@/integrations/supabase/client';

interface SensorStatusResponse {
  result: Array<{
    code: string;
    value: boolean | number | string;
  }>;
  success: boolean;
  t: number;
  tid: string;
}

interface SensorDetailsResponse {
  result: {
    id: string;
    name: string;
    online: boolean;
    status: Array<{
      code: string;
      value: boolean | number | string;
    }>;
    [key: string]: any;
  };
  success: boolean;
  t: number;
  tid: string;
}

interface SensorRealTimeData {
  isOnline: boolean;
  temperature: number;
  humidity: number;
  batteryLevel: number;
  lastUpdated: number;
}

export class TemperatureSensorService {
  
  static async fetchSensorStatus(deviceId: string): Promise<SensorRealTimeData | null> {
    try {
      console.log(`[SENSOR API REQUEST] Fetching complete data for sensor: ${deviceId}`);
      
      // Use Supabase proxy to avoid CORS issues
      const [detailsResponse, statusResponse] = await Promise.all([
        supabase.functions.invoke('tuya-proxy', {
          body: { endpoint: `/device/${deviceId.toLowerCase()}`, method: 'GET' }
        }),
        supabase.functions.invoke('tuya-proxy', {
          body: { endpoint: `/device/${deviceId.toLowerCase()}/status`, method: 'GET' }
        })
      ]);

      // Treat both a transport error AND a 200-with-error proxy body as offline.
      // (A 200 body with { error } / { success:false } would otherwise be parsed
      //  as if valid; default to offline instead of trusting a failed response.)
      const detailsBodyFailed = detailsResponse.data?.error || detailsResponse.data?.success === false;
      const statusBodyFailed = statusResponse.data?.error || statusResponse.data?.success === false;
      if (detailsResponse.error || statusResponse.error || detailsBodyFailed || statusBodyFailed) {
        // Background poll: log only, never toast.
        console.log(`[SENSOR OFFLINE] Sensor ${deviceId}: Proxy error, marking as offline`);
        await this.updateSensorOfflineStatus(deviceId);
        return {
          isOnline: false,
          temperature: 0,
          humidity: 0,
          batteryLevel: 0,
          lastUpdated: Date.now()
        };
      }

      const detailsData = detailsResponse.data;
      const statusData = statusResponse.data;
      
      // Use the actual 'online' field from device details
      const isActuallyOnline = detailsData.result?.online || false;
      const parsedData = this.parseSensorData(statusData, isActuallyOnline);
      
      console.log(`[SENSOR API SUCCESS] Sensor ${deviceId} - Online: ${isActuallyOnline}, Temp: ${parsedData.temperature}°C, Humidity: ${parsedData.humidity}%, Battery: ${parsedData.batteryLevel}%`);
      
      // Update database with real online status
      if (isActuallyOnline) {
        await this.updateSensorOnlineStatus(deviceId, parsedData);
      } else {
        await this.updateSensorOfflineStatus(deviceId);
      }
      
      return parsedData;
    } catch (error) {
      console.error(`[SENSOR API ERROR] Failed to fetch complete data for sensor ${deviceId}:`, error);
      await this.updateSensorOfflineStatus(deviceId);
      return {
        isOnline: false,
        temperature: 0,
        humidity: 0,
        batteryLevel: 0,
        lastUpdated: Date.now()
      };
    }
  }

  static async fetchMultipleSensorsStatus(deviceIds: string[]): Promise<Map<string, SensorRealTimeData | null>> {
    console.log(`[SENSOR POLLING] Starting polling cycle for ${deviceIds.length} sensors`);
    const results = new Map<string, SensorRealTimeData | null>();
    
    // Fetch all sensors in parallel for better performance
    const promises = deviceIds.map(async (deviceId) => {
      const data = await this.fetchSensorStatus(deviceId);
      return { deviceId, data };
    });
    
    const responses = await Promise.allSettled(promises);
    
    responses.forEach((response, index) => {
      const deviceId = deviceIds[index];
      if (response.status === 'fulfilled') {
        results.set(deviceId, response.value.data);
        console.log(`[SENSOR STATUS UPDATE] Sensor ${deviceId}: Online=${response.value.data?.isOnline}, Temp=${response.value.data?.temperature}°C, Humidity=${response.value.data?.humidity}%, Battery=${response.value.data?.batteryLevel}%`);
      } else {
        console.log(`[SENSOR OFFLINE] Sensor ${deviceId}: Promise rejected, marking as offline`);
        results.set(deviceId, {
          isOnline: false,
          temperature: 0,
          humidity: 0,
          batteryLevel: 0,
          lastUpdated: Date.now()
        });
      }
    });
    
    console.log(`[SENSOR POLLING] Completed polling cycle - ${deviceIds.length} sensors processed`);
    return results;
  }

  private static parseSensorData(response: SensorStatusResponse, actualOnlineStatus: boolean): SensorRealTimeData {
    const result = Array.isArray(response?.result) ? response.result : [];
    
    // Extract specific values from the result array
    const temperature = result.find(item => item.code === 'va_temperature')?.value as number || 0;
    const humidity = result.find(item => item.code === 'va_humidity')?.value as number || 0;
    const batteryLevel = result.find(item => item.code === 'battery_percentage')?.value as number || 
                        result.find(item => item.code === 'battery_state')?.value as number || 0;
    
    return {
      isOnline: actualOnlineStatus,
      temperature: actualOnlineStatus ? temperature / 10 : 0, // Temperature usually comes in 10ths
      humidity: actualOnlineStatus ? humidity : 0,
      batteryLevel: actualOnlineStatus ? batteryLevel : 0,
      lastUpdated: response.t
    };
  }

  // Update sensor status in database when online
  static async updateSensorOnlineStatus(deviceId: string, data: SensorRealTimeData): Promise<void> {
    try {
      const { error } = await supabase
        .from('temperature_sensors')
        .update({
          status: 'online',
          is_online: true,
          temperature: data.temperature,
          humidity: data.humidity,
          battery_level: data.batteryLevel,
          last_reading_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('device_id', deviceId);
      
      if (error) {
        console.error('Failed to update sensor online status:', error);
      }
    } catch (error) {
      console.error('Sensor database update error:', error);
    }
  }

  // Update sensor status in database when offline
  static async updateSensorOfflineStatus(deviceId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('temperature_sensors')
        .update({
          status: 'offline',
          is_online: false,
          updated_at: new Date().toISOString()
        })
        .eq('device_id', deviceId);
      
      if (error) {
        console.error('Failed to update sensor offline status:', error);
      }
    } catch (error) {
      console.error('Sensor database update error:', error);
    }
  }

  static formatLastUpdated(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
}